import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { groupBlocksByZone } from '../lib/cmsOverlayService';

/**
 * useListingCmsBlocks — Hook CMS léger pour pages listing hybrides
 * 
 * Charge les blocs CMS d'une page par ID, les groupe par zone.
 * Fallback silencieux : si la page n'existe pas ou a 0 blocs,
 * retourne des zones vides sans erreur visible.
 * 
 * Pattern réutilisable pour toute page listing :
 *   /partners, /products, /search (futur)
 * 
 * Usage:
 *   const { blocksByZone, hasCmsContent, loading } = useListingCmsBlocks('page-partners');
 * 
 * @param {string} pageId - ID de la page CMS (ex: 'page-partners')
 * @returns {{ blocksByZone, hasCmsContent, loading }}
 */
const useListingCmsBlocks = (pageId) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pageId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/pages/${pageId}`);
        if (cancelled) return;
        const activeBlocks = (response.data?.blocks || []).filter(b => b.is_active !== false);
        setBlocks(activeBlocks);
      } catch {
        // Fallback silencieux — page inexistante ou erreur réseau
        // La page listing continue de fonctionner sans CMS
        if (!cancelled) setBlocks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  const blocksByZone = useMemo(() => {
    if (!blocks.length) return { header: [], content: [], footer: [] };
    return groupBlocksByZone(blocks);
  }, [blocks]);

  const hasCmsContent = blocks.length > 0;

  return { blocksByZone, hasCmsContent, loading };
};

export default useListingCmsBlocks;
